import { type FormEvent, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Alert, Button, Field } from '../components/ui';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { errorMessage } from '../lib/errors';
import { AuthShell } from './LoginPage';

/** Segundo paso del login. El token del desafío llega por el estado de la navegación, nunca por la URL. */
export function MfaPage() {
  const { verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = location.state as { mfaToken?: string; from?: string } | null;
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!nav?.mfaToken) return <Navigate to="/login" replace />;
  const mfaToken = nav.mfaToken;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyMfa(mfaToken, code.trim());
      navigate(nav?.from ?? '/', { replace: true });
    } catch (err) {
      // 401 genérico (sin mensaje propio de la API) = el desafío venció: volver a empezar.
      if (err instanceof ApiError && err.status === 401 && err.message === 'Unauthorized') {
        navigate('/login', { replace: true, state: {} });
        return;
      }
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t('mfa.titulo')}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <p className="text-sm text-slate-600">{recovery ? t('mfa.instruccion_recuperacion') : t('mfa.instruccion')}</p>
        {error && <Alert>{error}</Alert>}
        <Field
          id="codigo"
          label={t('mfa.codigo')}
          autoComplete="one-time-code"
          inputMode={recovery ? 'text' : 'numeric'}
          maxLength={recovery ? 11 : 6}
          autoFocus
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button type="submit" className="w-full" disabled={busy || code.trim().length < 6}>
          {busy ? t('comun.cargando') : t('mfa.verificar')}
        </Button>
        <Button
          type="button"
          variant="link"
          onClick={() => {
            setRecovery(!recovery);
            setCode('');
            setError(null);
          }}
        >
          {recovery ? t('mfa.usar_aplicacion') : t('mfa.usar_recuperacion')}
        </Button>
      </form>
    </AuthShell>
  );
}
